import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";
import {
  LIVE_CHAT_COPY,
  LIVE_CHAT_LIMITS,
  LIVE_CHAT_MODES,
  LIVE_CHAT_SOCKET_CLOSE_CODES,
  LIVE_CHAT_SOCKET_EVENTS,
} from "../constants";
import {
  bootstrapLiveChat,
  getApiErrorMessage,
  getLiveChatHistory,
  hasApiError,
  sendLiveChatMessage,
  sendLiveChatOtp,
  verifyLiveChatOtp,
} from "../services/liveChatTransport";
import {
  createClientMessageId,
  createLiveChatStorage,
  createVisitorId,
} from "../utils/liveChatStorage";
import { createPendingMessage, getPendingMessages, mergeMessages } from "../utils/messages";
import {
  getVerificationState,
  hasUnansweredCustomerMessage,
  initialLiveChatState,
  isSupportedVerification,
  liveChatReducer,
  requiresInitialVerification,
} from "../state/liveChatReducer";
import {
  getLiveChatWsBaseUrl,
  resolveSocketUrl,
} from "../utils/liveChatUrls";
import { createHeartbeat, isHeartbeatAck } from "../utils/liveChatHeartbeat";


export function useLiveChat({
  liveChatConfig,
  merchantId,
  domain,
  mode = LIVE_CHAT_MODES.public,
  baseUrl,
  staffToken,
  active = true,
}) {
  const storage = useMemo(
    () => createLiveChatStorage({ merchantId, domain, mode }),
    [domain, merchantId, mode]
  );
  const restoredPending = useMemo(
    () =>
      storage.readPending().map((message) => ({
        ...createPendingMessage(message.client_message_id, message.message),
        created_at: message.created_at,
        pending: !message.failed,
        failed: Boolean(message.failed),
        waitingForVerification: Boolean(message.waitingForVerification),
        error: message.error || null,
        retryAt: message.retryAt || null,
      })),
    [storage]
  );
  const [state, dispatch] = useReducer(liveChatReducer, {
    ...initialLiveChatState,
    messages: restoredPending,
    token: storage.readToken(),
  });
  const stateRef = useRef(state);
  const mountedRef = useRef(true);
  const bootstrapPromiseRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const socketRef = useRef(null);
  const recoveryAttemptedRef = useRef(new Set());
  const visitorSessionTokenRef = useRef(storage.readVisitor()?.visitor_session_token);
  const historyReconciliationRef = useRef(null);
  const hasSocketConnectedRef = useRef(false);
  const messageRequestInFlightRef = useRef(false);
  const sendActiveHeartbeatRef = useRef(null);
  const activeRef = useRef(Boolean(active));
  const otpRequestInFlightRef = useRef(false);
  activeRef.current = Boolean(active);

  const canSendMessage = useCallback(() => {
    return (
      stateRef.current.verification?.message_send_allowed === true &&
      !stateRef.current.awaitingAiReply &&
      !messageRequestInFlightRef.current
    );
  }, []);

  useEffect(() => {
    sendActiveHeartbeatRef.current?.();
  }, [active]);

  useEffect(() => {
    stateRef.current = state;
    storage.writePending(getPendingMessages(state.messages));
  }, [state, storage]);

  useEffect(() => () => {
    mountedRef.current = false;
    historyReconciliationRef.current?.controller?.abort();
    historyReconciliationRef.current = null;
  }, []);

  const bootstrap = useCallback(
    async ({ forceFresh = false } = {}) => {
      if (bootstrapPromiseRef.current) return bootstrapPromiseRef.current;

      bootstrapPromiseRef.current = (async () => {
        dispatch({ type: "BOOTSTRAP_START" });
        let storedVisitor = forceFresh ? null : storage.readVisitor();
        let visitorId = storedVisitor?.visitor_id || createVisitorId();
        let lastError;

        for (let attempt = 0; attempt < LIVE_CHAT_LIMITS.bootstrapAttempts; attempt += 1) {
          const useFreshVisitor = forceFresh;
          const bootstrapUrl =
            mode === LIVE_CHAT_MODES.preview
              ? liveChatConfig.preview_bootstrap_url
              : liveChatConfig.bootstrap_url;

          try {
            const data = await bootstrapLiveChat({
              url: bootstrapUrl,
              baseUrl,
              staffToken: mode === LIVE_CHAT_MODES.preview ? staffToken : null,
              withCredentials: mode === LIVE_CHAT_MODES.preview,
              body: {
                ...((mode === LIVE_CHAT_MODES.public && domain
                  ? { shopify_domain: domain }
                  : mode === LIVE_CHAT_MODES.public && merchantId
                    ? { merchant_id: merchantId }
                    : {})),
                visitor_id: visitorId,
                ...(storedVisitor?.visitor_session_token && !useFreshVisitor
                  ? { visitor_session_token: storedVisitor.visitor_session_token }
                  : {}),
                page_url: window.location.href,
                referrer: document.referrer || "",
              },
            });

            if (!mountedRef.current) return data;
            const verification = getVerificationState(data.verification);
            const bootstrapData = { ...data, verification };
            storage.writeVisitor({
              visitor_id: data.visitor_id,
              visitor_session_token: data.visitor_session_token,
              conversation_id: data.conversation_id,
              last_opened_at: new Date().toISOString(),
            });
            visitorSessionTokenRef.current = data.visitor_session_token;
            storage.writeToken(data.token);
            const unsupported = !isSupportedVerification(verification);
            dispatch({
              type: "BOOTSTRAP_SUCCESS",
              data: bootstrapData,
              unsupported,
              identityStep: requiresInitialVerification(verification) ? "phone" : null,
            });
            return bootstrapData;
          } catch (error) {
            lastError = error;
            const invalidStoredSession = hasApiError(error, "visitor_session_token");
            if (invalidStoredSession || attempt === LIVE_CHAT_LIMITS.bootstrapAttempts - 2) {
              storage.clearVisitor();
              storage.clearToken();
              storedVisitor = null;
              forceFresh = true;
              visitorId = createVisitorId();
            }
          }
        }

        if (mountedRef.current) {
          dispatch({ type: "BOOTSTRAP_ERROR", error: getApiErrorMessage(lastError) || LIVE_CHAT_COPY.unavailable });
        }
        throw lastError;
      })().finally(() => {
        bootstrapPromiseRef.current = null;
      });

      return bootstrapPromiseRef.current;
    }, [baseUrl, domain, liveChatConfig, merchantId, mode, staffToken, storage]
  );

  const loadHistoryWithToken = useCallback(
    async (token, beforeId = null, signal = undefined) => {
      return getLiveChatHistory({
        url: liveChatConfig.history_url,
        token,
        beforeId,
        limit: LIVE_CHAT_LIMITS.historyPageSize,
        baseUrl,
        signal,
      });
    },
    [baseUrl, liveChatConfig.history_url]
  );

  const reconcileHistoryWithToken = useCallback(
    (token) => {
      if (historyReconciliationRef.current?.token === token) {
        return historyReconciliationRef.current.request;
      }

      historyReconciliationRef.current?.controller?.abort();
      const controller = new AbortController();
      const request = loadHistoryWithToken(token, null, controller.signal)
        .then((data) => {
          if (mountedRef.current) dispatch({ type: "OLDER_DONE", data });
          return data;
        })
        .finally(() => {
          if (historyReconciliationRef.current?.request === request) {
            historyReconciliationRef.current = null;
          }
        });

      historyReconciliationRef.current = { token, request, controller };
      return request;
    },
    [loadHistoryWithToken]
  );

  useEffect(() => {
    const hadStoredVisitor = Boolean(storage.readVisitor());
    bootstrap()
      .then(async (data) => {
        if (
          !hadStoredVisitor ||
          !(data.messages?.length || data.next_before_id || data.routing?.ticket_id)
        ) return;
        try {
          await reconcileHistoryWithToken(data.token);
        } catch {
          // Bootstrap messages remain a valid first-render fallback.
        }
      })
      .catch(() => {});
  }, [bootstrap, reconcileHistoryWithToken, storage]);

  useEffect(() => {
    if (state.status !== "ready" || !state.token || !state.wsUrl) {
      return undefined;
    }
    let reconnectTimer;
    let heartbeatTimer;
    let heartbeatAckTimer;
    let outstandingHeartbeatId = null;
    let activeHeartbeatPending = false;
    let intentionallyClosed = false;

    const stopHeartbeat = () => {
      window.clearInterval(heartbeatTimer);
      window.clearTimeout(heartbeatAckTimer);
      heartbeatTimer = undefined;
      heartbeatAckTimer = undefined;
      outstandingHeartbeatId = null;
      activeHeartbeatPending = false;
      sendActiveHeartbeatRef.current = null;
    };

    const startHeartbeat = (socket) => {
      stopHeartbeat();

      const sendHeartbeat = (queueIfOutstanding = false) => {
        if (socket.readyState !== WebSocket.OPEN) return;
        if (outstandingHeartbeatId) {
          if (queueIfOutstanding) activeHeartbeatPending = true;
          return;
        }

        activeHeartbeatPending = false;
        const heartbeat = {
          ...createHeartbeat(),
          chat_active: activeRef.current,
        };
        outstandingHeartbeatId = heartbeat.client_event_id;
        socket.send(JSON.stringify(heartbeat));

        heartbeatAckTimer = window.setTimeout(() => {
          if (
            socket.readyState === WebSocket.OPEN &&
            outstandingHeartbeatId === heartbeat.client_event_id
          ) {
            socket.close(
              LIVE_CHAT_SOCKET_CLOSE_CODES.heartbeatTimeout,
              "heartbeat_timeout"
            );
          }
        }, LIVE_CHAT_LIMITS.heartbeatAckTimeoutMs);
      };

      sendActiveHeartbeatRef.current = () => sendHeartbeat(true);
      sendHeartbeat();
      heartbeatTimer = window.setInterval(
        sendHeartbeat,
        LIVE_CHAT_LIMITS.heartbeatIntervalMs
      );
    };

    const connect = () => {
      if (!navigator.onLine || intentionallyClosed) return;
      if (
        socketRef.current?.readyState === WebSocket.OPEN ||
        socketRef.current?.readyState === WebSocket.CONNECTING
      ) {
        return;
      }
      let socket;
      try {
        const socketUrl = new URL(
          resolveSocketUrl(state.wsUrl, baseUrl, getLiveChatWsBaseUrl())
        );
        socketUrl.searchParams.set(
          "chat_active",
          activeRef.current ? "1" : "0"
        );
        socket = new WebSocket(socketUrl.toString());
      } catch {
        dispatch({ type: "SET_ERROR", error: LIVE_CHAT_COPY.unavailable });
        return;
      }
      socketRef.current = socket;
      socket.onopen = () => {
        const isRecoveredConnection =
          hasSocketConnectedRef.current || reconnectAttemptRef.current > 0;
        reconnectAttemptRef.current = 0;
        hasSocketConnectedRef.current = true;
        startHeartbeat(socket);
        if (isRecoveredConnection) {
          reconcileHistoryWithToken(stateRef.current.token).catch(() => {
            // The recovered socket remains usable if history reconciliation fails.
          });
        }
      };
      socket.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data);
          if (isHeartbeatAck(payload, outstandingHeartbeatId)) {
            window.clearTimeout(heartbeatAckTimer);
            heartbeatAckTimer = undefined;
            outstandingHeartbeatId = null;
            if (payload.routing) {
              dispatch({
                type: "ROUTING_MESSAGES",
                messages: [],
                routing: payload.routing,
              });
            }
            if (activeHeartbeatPending) sendActiveHeartbeatRef.current?.();
            return;
          }
          if (
            payload?.success &&
            payload?.type === LIVE_CHAT_SOCKET_EVENTS.message &&
            payload.data
          ) {
            dispatch({
              type: "ROUTING_MESSAGES",
              messages: [payload.data],
              routing: payload.routing,
            });
          }
        } catch {
          // Malformed socket events are ignored instead of reaching the render tree.
        }
      };
      socket.onclose = (event) => {
        stopHeartbeat();
        if (socketRef.current === socket) socketRef.current = null;
        if (intentionallyClosed) return;
        if (!navigator.onLine) return;
        if (event.code === LIVE_CHAT_SOCKET_CLOSE_CODES.unauthorized) {
          bootstrap().catch(() => {});
          return;
        }
        const exponent = reconnectAttemptRef.current++;
        const delay = Math.min(
          LIVE_CHAT_LIMITS.reconnectBaseDelayMs * 2 ** exponent,
          LIVE_CHAT_LIMITS.reconnectMaxDelayMs
        );
        reconnectTimer = window.setTimeout(connect, delay * (0.75 + Math.random() * 0.5));
      };
    };

    const handleOnline = () => {
      window.clearTimeout(reconnectTimer);
      connect();
    };
    const handleOffline = () => {
      stopHeartbeat();
      if (
        socketRef.current?.readyState === WebSocket.OPEN ||
        socketRef.current?.readyState === WebSocket.CONNECTING
      ) {
        socketRef.current.close(
          LIVE_CHAT_SOCKET_CLOSE_CODES.browserOffline,
          "browser_offline"
        );
      }
    };
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    connect();

    return () => {
      intentionallyClosed = true;
      stopHeartbeat();
      window.clearTimeout(reconnectTimer);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      socketRef.current?.close(
        LIVE_CHAT_SOCKET_CLOSE_CODES.normal,
        "live_chat_closed"
      );
      socketRef.current = null;
    };
  }, [baseUrl, bootstrap, reconcileHistoryWithToken, state.status, state.token, state.wsUrl]);
  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key !== storage.visitorKey || !event.newValue) return;
      try {
        const nextVisitor = JSON.parse(event.newValue);
        if (nextVisitor.visitor_session_token === visitorSessionTokenRef.current) return;
        visitorSessionTokenRef.current = nextVisitor.visitor_session_token;
        bootstrap().catch(() => {});
      } catch {
        // Invalid cross-tab storage data is ignored.
      }
    };
    window.addEventListener("storage", handleStorage);

    const channel =
      typeof BroadcastChannel === "undefined" ? null : new BroadcastChannel(storage.namespace);
    if (channel) {
      channel.onmessage = (event) => {
        if (event.data?.type === "SESSION_ROTATED") bootstrap().catch(() => {});
      };
    }

    return () => {
      window.removeEventListener("storage", handleStorage);
      channel?.close();
    };
  }, [bootstrap, storage.namespace, storage.visitorKey]);

  const sendExistingMessage = useCallback(
    async (clientMessageId, message) => {
      if (!canSendMessage()) return false;

      messageRequestInFlightRef.current = true;
      dispatch({ type: "MESSAGE_REQUEST_START" });

      try {
        let token = stateRef.current.token;
        if (!token) token = (await bootstrap()).token;

        const request = (activeToken) =>
          sendLiveChatMessage({
            url: liveChatConfig.message_url,
            token: activeToken,
            clientMessageId,
            message,
            baseUrl,
          });

        let data;
        try {
          data = await request(token);
        } catch (error) {
          if (error.status !== 401) throw error;
          const refreshed = await bootstrap();
          data = await request(refreshed.token);
        }

        const returnedMessages = [data.message, data.offline_message].filter(Boolean);
        dispatch({
          type: "ROUTING_MESSAGES",
          messages: returnedMessages,
          routing: data.routing,
        });
        const verification = getVerificationState(
          data.verification,
          stateRef.current.verification
        );
        dispatch({ type: "UPDATE_VERIFICATION", verification });
        const messages = mergeMessages(stateRef.current.messages, returnedMessages);
        stateRef.current = {
          ...stateRef.current,
          messages,
          verification,
          routing: data.routing || stateRef.current.routing,
          awaitingAiReply:
            (data.routing || stateRef.current.routing)?.assignee_type === "ai" &&
            hasUnansweredCustomerMessage(messages),
        };
        if (data.offline_identity_required) dispatch({ type: "VERIFICATION", verification });
        return true;
      } catch (error) {
        const verificationRequired = hasApiError(error, "verification");
        if (verificationRequired) {
          const errorData = error.payload?.data;
          const verification = getVerificationState(
            errorData?.verification || errorData,
            stateRef.current.verification
          );
          dispatch({ type: "VERIFICATION", verification });
          stateRef.current = { ...stateRef.current, verification, identityStep: "phone" };
          dispatch({ type: "MESSAGE_WAITING_FOR_VERIFICATION", clientMessageId });
          return false;
        }
        const messageText =
          error.status === 409
            ? "Message is still being processed. Retry shortly."
            : error.status === 429
              ? `Too many requests. Retry in ${error.retryAfter || 60} seconds.`
              : getApiErrorMessage(error) || (navigator.onLine ? LIVE_CHAT_COPY.unavailable : LIVE_CHAT_COPY.offline);
        dispatch({
          type: "MESSAGE_FAILED",
          clientMessageId,
          error: messageText,
          retryAt: error.status === 429 ? Date.now() + (error.retryAfter || 60) * 1000 : null,
        });
        return false;
      } finally {
        messageRequestInFlightRef.current = false;
        dispatch({ type: "MESSAGE_REQUEST_END" });
      }
    },
    [baseUrl, bootstrap, canSendMessage, liveChatConfig.message_url]
  );

  useEffect(() => {
    if (state.status !== "ready") return;
    restoredPending.forEach((pendingMessage) => {
      const clientMessageId = pendingMessage.client_message_id;
      const stillPending = stateRef.current.messages.some(
        (message) => message.client_message_id === clientMessageId
      );
      if (!stillPending || recoveryAttemptedRef.current.has(clientMessageId)) return;
      recoveryAttemptedRef.current.add(clientMessageId);
      sendExistingMessage(clientMessageId, pendingMessage.message);
    });
  }, [restoredPending, sendExistingMessage, state.status]);

  const sendMessage = useCallback(
    async (message) => {
      const trimmedMessage = message.trim();
      if (
        !trimmedMessage ||
        !canSendMessage()
      ) {
        return false;
      }
      const clientMessageId = createClientMessageId();
      dispatch({ type: "ADD_PENDING", message: createPendingMessage(clientMessageId, trimmedMessage) });
      return sendExistingMessage(clientMessageId, trimmedMessage);
    },
    [canSendMessage, sendExistingMessage]
  );

  const retryMessage = useCallback(
    (clientMessageId) => {
      const message = stateRef.current.messages.find(
        (candidate) => candidate.client_message_id === clientMessageId
      );
      if (
        !message ||
        !canSendMessage() ||
        (message.retryAt && message.retryAt > Date.now())
      ) {
        return;
      }
      dispatch({ type: "MESSAGE_RETRYING", clientMessageId });
      sendExistingMessage(clientMessageId, message.message);
    },
    [canSendMessage, sendExistingMessage]
  );

  const cancelMessage = useCallback((clientMessageId) => {
    dispatch({ type: "REMOVE_PENDING", clientMessageId });
  }, []);

  const loadOlder = useCallback(async () => {
    if (!stateRef.current.nextBeforeId || stateRef.current.loadingOlder) return;
    dispatch({ type: "OLDER_START" });
    try {
      let token = stateRef.current.token;
      let data;
      try {
        data = await loadHistoryWithToken(token, stateRef.current.nextBeforeId);
      } catch (error) {
        if (error.status !== 401) throw error;
        token = (await bootstrap()).token;
        data = await loadHistoryWithToken(token, stateRef.current.nextBeforeId);
      }
      dispatch({ type: "OLDER_DONE", data });
    } catch {
      dispatch({
        type: "OLDER_ERROR",
        error: "Couldn’t load older messages. Please try again in a few minutes.",
        retryAt: Date.now() + LIVE_CHAT_LIMITS.historyRetryDelayMs,
      });
    }
  }, [bootstrap, loadHistoryWithToken]);

  const setIdentityInput = useCallback((field, value) => {
    dispatch({ type: "IDENTITY_INPUT", field, value });
  }, []);

  const sendOtp = useCallback(async () => {
    if (otpRequestInFlightRef.current) return;
    const phone = stateRef.current.phone;
    if (!/^\d{10}$/.test(phone)) {
      dispatch({ type: "OTP_ERROR", error: "Enter a valid 10-digit phone number." });
      return;
    }
    otpRequestInFlightRef.current = true;
    dispatch({ type: "OTP_REQUEST_START", request: "sending" });
    try {
      const request = (token) =>
        sendLiveChatOtp({
          url: liveChatConfig.otp_send_url,
          token,
          phone,
          baseUrl,
        });
      try {
        await request(stateRef.current.token);
      } catch (error) {
        if (error.status !== 401) throw error;
        const refreshed = await bootstrap();
        await request(refreshed.token);
      }
      dispatch({ type: "OTP_SENT" });
    } catch (error) {
      dispatch({ type: "OTP_ERROR", error: getApiErrorMessage(error) || LIVE_CHAT_COPY.unavailable });
    } finally {
      otpRequestInFlightRef.current = false;
    }
  }, [baseUrl, bootstrap, liveChatConfig.otp_send_url]);

  const verifyOtp = useCallback(async () => {
    if (otpRequestInFlightRef.current) return;
    if (!/^\d{4}$/.test(stateRef.current.otp)) {
      dispatch({ type: "OTP_ERROR", error: "Enter a valid 4-digit OTP." });
      return;
    }
    otpRequestInFlightRef.current = true;
    dispatch({ type: "OTP_REQUEST_START", request: "verifying" });

    try {
      let data;
      try {
        data = await verifyLiveChatOtp({
          url: liveChatConfig.otp_verify_url,
          token: stateRef.current.token,
          phone: stateRef.current.phone,
          otp: stateRef.current.otp,
          baseUrl,
        });
      } catch (error) {
        if (error.status !== 401) throw error;
        const refreshed = await bootstrap();
        data = await verifyLiveChatOtp({
          url: liveChatConfig.otp_verify_url,
          token: refreshed.token,
          phone: stateRef.current.phone,
          otp: stateRef.current.otp,
          baseUrl,
        });
      }
      if (!data.verified) {
        dispatch({ type: "OTP_ERROR", error: LIVE_CHAT_COPY.invalidOtp });
        return;
      }

      const conversation = {
        ...data.conversation,
        verification: getVerificationState(data.conversation?.verification),
      };
      const pendingToRetry = stateRef.current.messages.filter(
        (message) => message.waitingForVerification && message.client_message_id
      );
      const rotatedWsUrl =
        conversation.ws_url ||
        liveChatConfig.ws_url_template?.replace(
          "{token}",
          encodeURIComponent(conversation.token)
        ) ||
        stateRef.current.wsUrl;
      storage.writeVisitor({
        visitor_id: conversation.visitor_id,
        visitor_session_token: conversation.visitor_session_token,
        conversation_id: conversation.conversation_id,
        last_opened_at: new Date().toISOString(),
      });
      visitorSessionTokenRef.current = conversation.visitor_session_token;
      storage.writeToken(conversation.token);
      stateRef.current = {
        ...stateRef.current,
        token: conversation.token,
        wsUrl: rotatedWsUrl,
        verification: conversation.verification,
        identityStep: null,
        postVerificationLoading: true,
      };
      socketRef.current?.close();
      dispatch({
        type: "OTP_VERIFIED",
        conversation,
        wsUrl: rotatedWsUrl,
        offlineMessage: data.offline_confirmation_message,
      });
      try {
        await reconcileHistoryWithToken(conversation.token);
      } catch {
        // The verified conversation remains usable even if reconciliation fails.
      } finally {
        if (mountedRef.current) {
          dispatch({ type: "VERIFIED_CHAT_READY" });
        }
      }
      pendingToRetry.forEach((message) => {
        sendExistingMessage(message.client_message_id, message.message);
      });
      if (typeof BroadcastChannel !== "undefined") {
        const channel = new BroadcastChannel(storage.namespace);
        channel.postMessage({ type: "SESSION_ROTATED" });
        channel.close();
      }
    } catch (error) {
      dispatch({ type: "OTP_ERROR", error: getApiErrorMessage(error) || LIVE_CHAT_COPY.invalidOtp });
    } finally {
      otpRequestInFlightRef.current = false;
    }
  }, [baseUrl, bootstrap, liveChatConfig, reconcileHistoryWithToken, sendExistingMessage, storage]);

  return {
    ...state,
    sendMessage,
    retryMessage,
    cancelMessage,
    loadOlder,
    setIdentityInput,
    sendOtp,
    verifyOtp,
    editPhone: () => dispatch({ type: "EDIT_PHONE" }),
    retryBootstrap: () => bootstrap({ forceFresh: false }).catch(() => {}),
  };
}
